import {MediaCardType} from '@wandb/weave/common/types/media';
import {useMemo} from 'react';

import {Layout, MediaConfigWithDefaultsAndMediaKeys} from '../Component';
import {PERFORMANCE_THRESHOLDS} from '../layout';
import {TilePagination} from '../tiles';

/**
 * Calculates tile pagination for performance optimization.
 *
 * Paginates tiles when displaying large datasets:
 * - Plotly: Limited to 4 per page (performance-intensive)
 * - Images: Limited to maintain full grid appearance (32 max, adjusted by column count)
 * - Other media types: No pagination
 *
 * @param layout - Generated layout with tiles
 * @param config - Panel configuration with mode and pagination settings
 * @param mediaType - Type of media being displayed
 * @returns Pagination info or null if no pagination needed
 */
export function useTilePagination(
  layout: Layout,
  config: MediaConfigWithDefaultsAndMediaKeys | null,
  mediaType: MediaCardType | null
): TilePagination | null {
  return useMemo(() => {
    // Only paginate in gallery mode
    if (config?.mode !== 'gallery') {
      return null;
    }

    const columnCount = config?.columnCount ?? 1;
    let maxTiles = null;

    if (mediaType === 'plotly') {
      // Plotly cards perform poorly past 8 on a page. This value allows us to
      // perform well with the default 2 panels in a section open.
      maxTiles = PERFORMANCE_THRESHOLDS.MAX_PLOTLY_WITHOUT_PAGINATION;
    } else if (mediaType === 'image') {
      // In addition, images may be quite large, so we should paginate them at
      // some reasonable boundary, while also trying to maintain an attractive
      // grid-looking UI where possible.
      //
      // The notion is that each page of data should look "full" (i.e. it
      // appears as a grid where every cell is populated) as much as possible,
      // except for the last page which can be partially-empty. This should be
      // true regardless of the number of configured columns in the UI. Some
      // examples:
      //
      // If the user picks 8 columns, they get a maximum of four rows (32 max
      // images / 8 columns = 4 rows). If they pick 11 columns, they get a
      // maximum of two rows (32 max images / 11 columns = ~2.9 rows, round down
      // to 2), but where every grid cell will be populated unless it's the last
      // page. This means there are less-great column count choices (>=17 yields
      // only a single row), but there's a consistent expectation in the UI that
      // if the grid is fully-populated there's probably another page of data to
      // look at.
      //
      maxTiles =
        Math.floor(
          PERFORMANCE_THRESHOLDS.MAX_IMAGES_WITHOUT_PAGINATION / columnCount
        ) * columnCount;
    }

    // No pagination needed if within limits
    if (maxTiles == null || maxTiles >= layout.tiles.length) {
      return null;
    }

    return {
      start: config?.page?.start ?? 0,
      size: maxTiles,
      total: layout.tiles.length,
    };
  }, [
    config?.mode,
    mediaType,
    config?.columnCount,
    layout.tiles.length,
    config?.page?.start,
  ]);
}
